import { useEffect, useState } from "react";

// Maps common US state region codes to our supported city keys
const STATE_TO_CITY = {
  FL: "miami",
  ME: "cumberland",
  DC: "dc",
};

const CACHE_KEY = "visitor_state_v1";

/**
 * Detects the visitor's US state via free ip-geolocation (ipapi.co) and
 * returns a normalized city key: "miami" | "cumberland" | "dc" | null.
 * Result is cached in sessionStorage to avoid repeat lookups per session.
 */
export const useVisitorState = () => {
  const [city, setCity] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Read from sessionStorage first
    try {
      const cached = window.sessionStorage.getItem(CACHE_KEY);
      if (cached !== null) {
        setCity(cached || null);
        return;
      }
    } catch (err) {
      console.debug("[useVisitorState] sessionStorage read failed:", err);
      // sessionStorage unavailable — proceed with fetch
    }

    let cancelled = false;
    const ac = new AbortController();
    const timeoutId = setTimeout(() => ac.abort(), 3500);

    fetch("https://ipapi.co/json/", { signal: ac.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        const code = data?.region_code || data?.region || "";
        const matched = STATE_TO_CITY[String(code).toUpperCase()] || "";
        try {
          window.sessionStorage.setItem(CACHE_KEY, matched);
        } catch (err) {
          console.debug("[useVisitorState] sessionStorage write failed:", err);
        }
        setCity(matched || null);
      })
      .catch((err) => {
        // Network or abort — cache empty so we don't keep retrying this session
        console.debug("[useVisitorState] geolocation lookup failed:", err);
        try {
          window.sessionStorage.setItem(CACHE_KEY, "");
        } catch (storageErr) {
          console.debug("[useVisitorState] sessionStorage write failed:", storageErr);
        }
      })
      .finally(() => {
        clearTimeout(timeoutId);
      });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      ac.abort();
    };
  }, []);

  return city;
};
